import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect } from 'react'

import { FormInstructions } from '../../../components'
import { usePeerGroups } from '../../../providers/PeerGroups/PeerGroups.provider'
import { PeerGroupListItem } from '../../../providers/PeerGroups/types'
import { useProviders } from '../../../providers/ProvidersProvider/ProvidersProvider.provider'
import { Provider } from '../../../providers/ProvidersProvider/types'
import { themeColors } from '../../../theme'
import {
  FieldObjectEditable,
  FieldObjectNonEditable,
} from '../../../utilities/Forms/FieldObject.partial'
import { Field, SectionFields } from '../../../utilities/Forms/types'
import { Section } from '../../../utilities/Forms/types'
import { Announcement } from '../../Dashboard/Widgets/Announcement/Announcement.container'
import { ScheduleMode, SendToOption } from './Announcements.container'

// Define types for our dropdown options
interface AnnouncementsViewProps {
  user: any
  announcementDetailFields: SectionFields
  announcementCreated: boolean
  setAnnouncementCreated: React.Dispatch<React.SetStateAction<boolean>>
  announcementSubmitting: boolean
  setAnnouncementSubmitting: React.Dispatch<React.SetStateAction<boolean>>
  sendToOption: SendToOption
  setSendToOption: React.Dispatch<React.SetStateAction<SendToOption>>
  selectedProvider: Provider | null
  setSelectedProvider: React.Dispatch<React.SetStateAction<Provider | null>>
  selectedPeerGroup: PeerGroupListItem | null
  setSelectedPeerGroup: React.Dispatch<
    React.SetStateAction<PeerGroupListItem | null>
  >
  scheduleMode: ScheduleMode
  setScheduleMode: React.Dispatch<React.SetStateAction<ScheduleMode>>
  scheduledDateTime: string
  setScheduledDateTime: React.Dispatch<React.SetStateAction<string>>
}

export const AnnouncementsView = ({
  user,
  announcementDetailFields,
  announcementCreated,
  setAnnouncementCreated,
  announcementSubmitting,
  sendToOption,
  setSendToOption,
  selectedProvider,
  setSelectedProvider,
  selectedPeerGroup,
  setSelectedPeerGroup,
  scheduleMode,
  setScheduleMode,
  scheduledDateTime,
  setScheduledDateTime,
}: AnnouncementsViewProps) => {
  const { providers, getAllProviders } = useProviders()
  const { peerGroups } = usePeerGroups()

  useEffect(() => {
    getAllProviders()
  }, [])

  // Handle send to option change
  const handleSendToChange = (event: SelectChangeEvent) => {
    setSendToOption(event.target.value as SendToOption)
    // Reset sub-selections when changing the main option
    setSelectedProvider(null)
    setSelectedPeerGroup(null)
  }

  // Handle provider selection change
  const handleProviderChange = (
    event: React.SyntheticEvent,
    newValue: Provider | null
  ) => {
    setSelectedProvider(newValue)
  }

  // Handle peer group selection change
  const handlePeerGroupChange = (
    event: React.SyntheticEvent,
    newValue: PeerGroupListItem | null
  ) => {
    setSelectedPeerGroup(newValue)
  }

  const renderAnnouncementSentNotice = () => {
    switch (sendToOption) {
      case 'all_patients':
        return `Announcement ${
          scheduleMode === 'scheduled' ? 'scheduled' : 'sent'
        } to all active patients.`
      case 'all_my_patients':
        return `Announcement ${
          scheduleMode === 'scheduled' ? 'scheduled' : 'sent'
        } to all my patients.`
      case 'provider_patients':
        return `Announcement ${
          scheduleMode === 'scheduled' ? 'scheduled' : 'sent'
        } to all patients of provider ${selectedProvider?.name} .`
      case 'peer_group':
        return `Announcement ${
          scheduleMode === 'scheduled' ? 'scheduled' : 'sent'
        } to all participants of ${selectedPeerGroup?.name}.`
      default:
        return 'Announcement sent to all active patients.'
    }
  }

  const getCurrentDateTime = () => {
    const now = new Date()
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
    return now.toISOString().slice(0, 16)
  }

  return (
    <Box data-testid='Announcement' sx={{ height: '100%' }}>
      {/* Title Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: '10px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '125%',
          }}
        >
          Announcements View
        </Typography>
      </Box>

      {/* Announcement Input or Confirmation */}
      <Box
        sx={{
          width: '100%',
          paddingRight: 2,
          justifyContent: 'space-around',
          height: '100%',
        }}
      >
        {!announcementCreated ? (
          <Box
            sx={{
              backgroundColor: themeColors.gray60,
              color: themeColors.white,
              position: 'sticky',
              height: 100,
              fontWeight: 'bold',
              width: '50%',
              padding: '10px 15px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '10px',
            }}
          >
            {renderAnnouncementSentNotice()}
          </Box>
        ) : (
          <Box
            id='column-left'
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              height: '100%',
            }}
          >
            {/* announcement table list */}
            {announcementDetailFields.map((section: Section) => (
              <Box
                key={section.sectionLabel}
                sx={{
                  display: 'flex',
                  width: '50%',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    marginBottom: 0,
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    {!!section.hasInstructions && announcementCreated && (
                      <FormInstructions isAnnouncement={true} />
                    )}
                  </Box>

                  {/* Send To Dropdown Section - Add this before field mapping */}
                  {announcementCreated && (
                    <Box sx={{ mb: 1, width: '300px' }}>
                      <FormControl fullWidth>
                        <InputLabel id='send-to-label'>Send To</InputLabel>
                        <Select
                          labelId='send-to-label'
                          id='send-to-select'
                          value={sendToOption}
                          label='Send To'
                          onChange={handleSendToChange}
                        >
                          <MenuItem value='all_patients'>All Patients</MenuItem>
                          <MenuItem value='all_my_patients'>
                            My Patients
                          </MenuItem>
                          <MenuItem value='provider_patients'>
                            All Patients of a Provider
                          </MenuItem>
                          <MenuItem value='peer_group'>
                            All Participants of a Peer Group
                          </MenuItem>
                        </Select>
                      </FormControl>

                      {/* Conditional Provider Dropdown */}
                      {sendToOption === 'provider_patients' && providers && (
                        <FormControl fullWidth>
                          <Autocomplete
                            id='provider-autocomplete'
                            options={providers}
                            getOptionLabel={(option) => option.name}
                            value={selectedProvider}
                            onChange={handleProviderChange}
                            isOptionEqualToValue={(option, value) =>
                              option.providerId === value.providerId
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label='Search Provider'
                                placeholder='Start typing provider name...'
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props} key={option.providerId}>
                                {option.name}
                              </li>
                            )}
                          />
                        </FormControl>
                      )}

                      {/* Conditional Peer Group Dropdown */}
                      {sendToOption === 'peer_group' && peerGroups && (
                        <FormControl fullWidth>
                          <Autocomplete
                            id='provider-autocomplete'
                            options={peerGroups}
                            getOptionLabel={(option) => option.name}
                            value={selectedPeerGroup}
                            onChange={handlePeerGroupChange}
                            isOptionEqualToValue={(option, value) =>
                              option.peerGroupId === value.peerGroupId
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label='Search Peer Group'
                                placeholder='Start typing Peer Group name...'
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props} key={option.peerGroupId}>
                                {option.name}
                              </li>
                            )}
                          />
                        </FormControl>
                      )}
                    </Box>
                  )}

                  {/* name / description fields */}
                  <Box sx={{ mb: 2, width: '300px' }}>
                    {section.fields.map((field: Field) => {
                      const fieldsWithValues: Field = {
                        ...field,
                        initialValue: field.displayKey
                          ? user[field.displayKey]
                          : user[field.backendKey],
                      }
                      return !announcementCreated ? (
                        <FieldObjectNonEditable
                          field={fieldsWithValues}
                          key={field.label}
                        />
                      ) : (
                        <FieldObjectEditable
                          field={fieldsWithValues}
                          key={field.label}
                        />
                      )
                    })}
                  </Box>

                  <Box
                    sx={{
                      p: 1,
                      border: `1px solid ${themeColors.gray70}`,
                      borderRadius: 1,
                      height: `${
                        scheduleMode === 'immediate'
                      } ? '150px' :'200px'`,
                    }}
                  >
                    <Typography fontWeight={700} fontSize={14} sx={{ mb: 1 }}>
                      Schedule Delivery
                    </Typography>

                    <RadioGroup
                      value={scheduleMode}
                      onChange={(e) =>
                        setScheduleMode(
                          e.target.value as 'immediate' | 'scheduled'
                        )
                      }
                    >
                      <FormControlLabel
                        value='immediate'
                        control={<Radio />}
                        label='Send immediately'
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                          },
                        }}
                      />
                      <FormControlLabel
                        value='scheduled'
                        control={<Radio />}
                        label='Schedule for later'
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                          },
                        }}
                      />
                    </RadioGroup>

                    {scheduleMode === 'scheduled' && (
                      <TextField
                        label='Scheduled Date & Time'
                        type='datetime-local'
                        value={scheduledDateTime}
                        onChange={(e) => setScheduledDateTime(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: getCurrentDateTime() }}
                        style={{ marginBottom: 10 }}
                      />
                    )}
                  </Box>

                  {!announcementCreated ? (
                    <Box
                      sx={{
                        display: 'flex',
                        margin: '10px',
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        variant='contained'
                        onClick={() => setAnnouncementCreated(true)}
                      >
                        Create a new Announcement
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        margin: '10px',
                        width: '100%',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          margin: '10px',
                          width: '100%',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          variant='contained'
                          type='submit'
                          disabled={announcementSubmitting}
                        >
                          {scheduleMode === 'scheduled'
                            ? 'Schedule Announcement'
                            : 'Send Announcement'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}

            <Box sx={{ minHeight: '700px', width: '80%' }}>
              <Announcement />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
